import ReactGA from 'react-ga4';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
/////////////////////////////////////////////////////////////
import { telegramWebApp } from '@/main';
import { useCustomDispatch, useCustomSelector } from '@/core/redux/hooks';
import { ActivitiesService } from '@/core/services';
import {
	setComboCards,
	setCounter,
	setDiamonds,
	setPendingBuildAction,
	setStickerWonDialog,
	setTotalCoinsGenerated,
} from '@/core/redux/slices';
import {
	Drawer,
	DrawerContent,
	DrawerClose,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	Button,
	Close,
	DrawerDescription,
} from '@/app/shared/components';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { ComboCard, ComboCardModel } from '@/core/interfaces';

interface RevealDailyComboProps {
	isOpen: boolean;
	onClose: React.Dispatch<React.SetStateAction<boolean>>;
}

export const RevealDailyCombo: React.FC<RevealDailyComboProps> = ({
	isOpen,
	onClose,
}: RevealDailyComboProps) => {
	/*********************
	// #region Hooks
	**********************/
	const dispatch = useCustomDispatch();
	const { t } = useTranslation();

	/*********************
	// #region Redux
	**********************/
	const { diamonds, levelCombo } = useCustomSelector(
		(state) => state.counter
	);
	const { rewardCollected } = useCustomSelector((state) => state.categories);

	/*********************
	// #region Functions
	**********************/
	const handleRequestRevealDailyCombo = async () => {
		telegramWebApp.HapticFeedback.impactOccurred('heavy');
		if (diamonds < levelCombo.diamondsCostReveal) {
			toast.warning(t('dailycombo.reveal.insufficient'));
			return;
		}

		toast.promise(ActivitiesService.revealDailyCombo(), {
			loading: t('general.loading'),
			success: (data: any) => {
				// Emit event to Google Analytics
				ReactGA.event({
					category: 'boosts',
					action: 'Reveal daily combo',
				});
				// Update counter
				dispatch(setCounter(data.currentCoins));
				// Update diamonds
				dispatch(setDiamonds(data.currentDiamonds));
				// Set total coins
				dispatch(setTotalCoinsGenerated(data.totalCoinsEarned));
				// Set combo cards
				if (
					data.data.userComboCards &&
					data.data.userComboCards.length > 0
				) {
					const comboCards: ComboCard[] = [];

					data.data.userComboCards.map((combo: ComboCardModel) => {
						comboCards.push({
							id: combo.id,
							image: combo.imageUrl,
							title: combo.thumbnail.title,
							description: combo.thumbnail.description,
						});
					});

					dispatch(
						setComboCards({
							comboCards: comboCards,
						})
					);

					if (
						data.data.userComboCards.length === 3 &&
						!rewardCollected
					) {
						dispatch(setPendingBuildAction(true));
					}
				}

				// Check if has a gift
				if (data.isAwardWinning) {
					dispatch(setStickerWonDialog(true));
				}

				return `${t('dailycombo.reveal.confirm')}`;
			},
			error: t('dailycombo.reveal.error'),
		});
		onClose(false);
	};

	/*********************
	// #region Template
	**********************/
	return (
		<Drawer open={isOpen} onOpenChange={onClose}>
			<DrawerContent>
				<div className="mx-auto mt-4 h-2 w-[100px] rounded-full bg-muted" />
				<div className="mx-auto w-full max-w-sm ">
					<DrawerHeader>
						<DrawerTitle>
							<VisuallyHidden>
								{t('dailycombo.reveal.title')}
							</VisuallyHidden>
							<DrawerClose className="right-5 text-right absolute">
								<Close fillcolor="#fff" />
							</DrawerClose>
						</DrawerTitle>
					</DrawerHeader>
					<DrawerDescription>
						<VisuallyHidden>
							{t('dailycombo.reveal.text', {
								amount: levelCombo.diamondsCostReveal,
							})}
						</VisuallyHidden>
					</DrawerDescription>

					<div className="mx-auto px-8">
						<img
							className="cursor-pointer mx-auto  mb-6"
							src={process.env.VITE_DIAMOND_IMAGE}
						/>

						<div className="flex flex-col text-center gap-3 mb-8">
							<p className="font-bold text-2xl">
								{t('dailycombo.reveal.title')}
							</p>
							<p className="font-semibold text-sm text-white text-center">
								{t('dailycombo.reveal.text', {
									amount: levelCombo.diamondsCostReveal,
								})}
							</p>
						</div>
					</div>
					<DrawerFooter>
						<DrawerClose asChild>
							<Button
								variant="primary"
								className="py-6 w-full text-sm rounded-lg"
								onClick={handleRequestRevealDailyCombo}
							>
								<div className="flex justify-center gap-1 items-center ">
									<p className="text-center font-bold leading-none">
										{t('dailycombo.reveal.buy')}{' '}
										{levelCombo.diamondsCostReveal}
									</p>
									<img
										src={
											process.env.VITE_MINI_DIAMOND_IMAGE
										}
										alt={t('hacks.exchangeDiamonds.text')}
										className="w-4 h-4"
									/>
								</div>
							</Button>
						</DrawerClose>
					</DrawerFooter>
				</div>
			</DrawerContent>
		</Drawer>
	);
};
